import { z } from 'zod';
import { LIMITES, LIMITES_MESTRE } from './limites';
import { MOTORES } from './motor';
import { DecisaoPlanoSchema, DecisaoResponderSchema } from './painel';

/**
 * Canal da sessão mestre (F13): as mensagens `mestre.*` que o daemon que a
 * hospeda emite no namespace `/agente`, com ack, e a leitura curada do
 * `.claude` de uma máquina.
 *
 * Tudo aqui é ACRÉSCIMO compatível: daemon antigo nunca emite `mestre.*` e a
 * API antiga sem os handlers deixa o ack estourar — a ferramenta MCP falha com
 * erro legível, nada quebra. Por isso `VERSAO_PROTOCOLO` fica onde está.
 *
 * A autorização é da API, não do schema: o socket precisa ser a máquina VPS
 * que hospeda o mestre vigente e vivo, e o escopo de leitura é o do usuário
 * que criou o mestre, recarregado do banco a cada chamada.
 */

/** Espelha o `EffortLevel` do SDK, como em `sdk.ts`. */
const Esforco = z.enum(['low', 'medium', 'high', 'xhigh', 'max']);

const EstadoDeSessao = z.enum(['ATIVA', 'DORMINDO', 'AGUARDANDO_PERMISSAO', 'ENCERRADA']);

/** Toda mensagem do canal carrega o id do mestre que a API vai conferir. */
const Base = { mestreId: z.string().min(1) };

/**
 * O filtro de motor de uma consulta, em TRÊS estados — e ele é `.optional()`
 * SEM `.default()`, o que é a decisão inteira deste campo.
 *
 * - **ausente**: "não falei de motor". Quem decide o que isso significa é a
 *   consulta, na API, e cada uma decide diferente: em `sessoes.listar` o padrão
 *   é a DESIGNAÇÃO do mestre (as do motor dele, que é o que ele cuida); em
 *   `mestres.listar` o padrão é TODOS, porque a consulta existe justamente para
 *   ele achar o par do outro motor.
 * - **um motor**: só aquele, mesmo que não seja o dele.
 * - **`'todos'`**: os dois, dito por extenso.
 *
 * É por causa daquela divergência que um `.default()` não serve: ele obrigaria
 * as duas consultas ao MESMO padrão, e `mestres.listar` passaria a devolver só
 * o próprio mestre — uma consulta que existe para achar o outro devolvendo
 * apenas quem perguntou. Com default, o servidor também perderia para sempre a
 * diferença entre "o mestre escolheu CLAUDE" e "o mestre não falou nada".
 *
 * `'todos'` é valor do enum, não `null`, porque um mestre pode querer os dois de
 * propósito — e "de propósito" tem de ser distinguível de "não falou".
 */
export const MotorDaConsultaSchema = z.enum([...MOTORES, 'todos'] as const).optional();
export type MotorDaConsulta = z.infer<typeof MotorDaConsultaSchema>;

/** Consulta de leitura (`mestre.consulta`): uma união por tipo. */
export const MestreConsultaSchema = z.discriminatedUnion('tipo', [
  z.object({ ...Base, tipo: z.literal('maquinas.listar') }),
  z.object({
    ...Base,
    tipo: z.literal('sessoes.listar'),
    maquinaId: z.string().min(1).optional(),
    estado: EstadoDeSessao.optional(),
    /** Filtro de texto barato sobre nome/título/pasta, aplicado na API. */
    texto: z.string().max(200).optional(),
    /** Ausente aqui significa a designação do mestre — as do motor dele. */
    motor: MotorDaConsultaSchema,
    limite: z.number().int().min(1).max(100).default(50),
  }),
  z.object({
    ...Base,
    tipo: z.literal('conversa.ler'),
    sessaoId: z.string().min(1),
    /** Cursor para trás: eventos com `seq` menor que este. */
    antesDeSeq: z.number().int().positive().optional(),
    // Aceita até 200 só para o daemon anterior ao R9; a API clampa em `conversaPorLeitura`.
    limite: z.number().int().min(1).max(200).default(LIMITES_MESTRE.conversaPorLeitura),
  }),
  z.object({
    ...Base,
    tipo: z.literal('busca'),
    termo: z.string().min(2).max(200),
    maquinaId: z.string().min(1).optional(),
    sessaoId: z.string().min(1).optional(),
    /** Ausente aqui significa a designacao do mestre. */
    motor: MotorDaConsultaSchema,
    limite: z.number().int().min(1).max(60).default(30),
  }),
  z.object({
    ...Base,
    tipo: z.literal('trabalhos.listar'),
    limite: z.number().int().min(1).max(100).default(30),
  }),
  z.object({ ...Base, tipo: z.literal('claude.ler'), maquinaId: z.string().min(1) }),
  z.object({
    ...Base,
    tipo: z.literal('pendencias.listar'),
    /** Ausente aqui significa a designacao do mestre. */
    motor: MotorDaConsultaSchema,
    limite: z.number().int().min(1).max(50).default(20),
  }),
  /*
    Os mestres da frota (`mestres.listar`) — a consulta que existe por uma
    ausência, não por conveniência.

    `escopoSql` ESCONDE as sessões MESTRE de `sessoes.listar` e da busca, de
    propósito (o transcript de um mestre repete conversa de outras máquinas).
    Sem uma consulta própria, um mestre não teria como descobrir o id do par, e
    "mande recado ao seu par" seria uma instrução de prompt sem como ser
    obedecida — promessa que o servidor não cumpre.

    Ela devolve SÓ mestres: nada de sessão normal atravessa por aqui, senão a
    porta viraria um contorno para o recorte que `escopoSql` aplica.

    Aqui o `motor` ausente significa TODOS (ver `MotorDaConsultaSchema`): pedir
    a lista para achar o outro e receber só a si mesmo seria inútil.
  */
  z.object({ ...Base, tipo: z.literal('mestres.listar'), motor: MotorDaConsultaSchema }),
]);
export type MestreConsulta = z.infer<typeof MestreConsultaSchema>;

/**
 * Despacho de mensagem para outra sessão (`mestre.mensagem`).
 *
 * O teto desconta 100 chars do texto de mensagem comum: a API prefixa a
 * moldura de origem (`[despacho da sessão mestre]`) ANTES de entregar, e o
 * conjunto precisa caber no `SdkEnviarSchema` do daemon do alvo.
 */
export const MestreMensagemSchema = z.object({
  ...Base,
  sessaoId: z.string().min(1),
  texto: z.string().min(1).max(LIMITES.textoMensagem - 100),
});
export type MestreMensagem = z.infer<typeof MestreMensagemSchema>;

/**
 * Renomear uma sessão da frota (`mestre.renomear`).
 *
 * Pedido de funcionalidade despachado pela própria mestre no primeiro dia:
 * ela enxergava títulos gêmeos e não tinha como desambiguar. Mesmo contrato
 * do painel (1–80, grava `nome`, que vence o autotítulo — regra da P2), vale
 * para qualquer estado (arrumar histórico é o caso de uso) e NÃO consome o
 * teto de despachos por turno: renomear quatro gêmeas numa tacada não pode
 * comer a cota de mensagens. A proteção fica no teto global por minuto.
 */
export const MestreRenomearSchema = z.object({
  ...Base,
  sessaoId: z.string().min(1),
  nome: z.string().min(1).max(80),
});
export type MestreRenomear = z.infer<typeof MestreRenomearSchema>;

/**
 * Resposta da mestre a uma pendência de PERGUNTA ou PLANO (`mestre.responder`).
 *
 * A união só carrega as duas pernas que a mestre pode usar: `responder` e
 * `plano`. `permitir`/`negar` (aprovar FERRAMENTA de outra sessão) ficam de
 * fora POR CONSTRUÇÃO — aprovar um Bash alheio é decisão do dono, e o schema
 * é a trava, não uma checagem que alguém esquece.
 */
export const MestreResponderSchema = z.object({
  ...Base,
  pedidoId: z.string().min(1),
  resposta: z.discriminatedUnion('decisao', [DecisaoResponderSchema, DecisaoPlanoSchema]),
});
export type MestreResponder = z.infer<typeof MestreResponderSchema>;

/**
 * Arquivar/desarquivar uma conversa (`mestre.arquivar`).
 *
 * Arquivar aqui é o gesto REVERSÍVEL do painel (`arquivadaEm`): sai da lista,
 * continua viva na máquina. Encerrar — que mata — não é poder da mestre; a
 * fronteira da D40 fica de pé. A própria mestre não se arquiva
 * (`mestre_nao_arquiva`): ela ocupa a vaga única enquanto existir, e sumir da
 * lista esconderia isso. Fora do teto de despachos, como o renomear:
 * organizar a lista em lote é o caso de uso.
 */
export const MestreArquivarSchema = z.object({
  ...Base,
  sessaoId: z.string().min(1),
  arquivada: z.boolean().default(true),
});
export type MestreArquivar = z.infer<typeof MestreArquivarSchema>;

/**
 * Adormecer uma conversa (`mestre.adormecer`).
 *
 * Dormir não é arquivar e não é encerrar — é o terceiro gesto, e o único que
 * resolve capacidade sem perder nada: o processo do Claude sai da memória (a
 * vaga volta na máquina E no painel) e a conversa fica inteira no histórico,
 * a um Retomar de distância. Arquivar só tira da lista, com a vaga presa;
 * encerrar devolve a vaga mas mata de vez.
 *
 * Sem `dormindo: false` para acordar: retomar exige vaga, memória e transcript
 * no disco, e é decisão de quem está na conversa. Fora do teto de despachos,
 * como o arquivar: liberar cinco vagas de uma vez é o caso de uso.
 */
export const MestreAdormecerSchema = z.object({
  ...Base,
  sessaoId: z.string().min(1),
});
export type MestreAdormecer = z.infer<typeof MestreAdormecerSchema>;

/**
 * Ligar/desligar a vigília de uma sessão (`mestre.acompanhar`).
 *
 * Com a vigília ligada, fim de turno (com a mesma política de silêncio dos
 * avisos) e pendência nova de pergunta/plano CUTUCAM a mestre com um recado
 * `[acompanhamento]` — acordando-a se estiver dormindo. É a vigilância ativa
 * que a D43 adiou, aberta pelo dono.
 */
export const MestreAcompanharSchema = z.object({
  ...Base,
  sessaoId: z.string().min(1),
  ligar: z.boolean(),
});
export type MestreAcompanhar = z.infer<typeof MestreAcompanharSchema>;

/**
 * Os modos que a mestre pode dar a uma sessão — na criação e na troca.
 *
 * D50 (2026-09-08, pedido do dono, revoga a fronteira de modos da D40): `auto`
 * e `bypassPermissions` entram, somando aos três que perguntam. Eles NÃO são a
 * mesma coisa, e a diferença está medida no SDK (`sdk.d.ts:2300`): `auto` usa
 * um classificador de modelo que aprova, NEGA e escala para humano em safety
 * check; `bypassPermissions` não checa nada. Por isso só o segundo é
 * `perigoso: true` e exige `confirmacaoModoPerigoso`.
 *
 * `dontAsk` fica de fora de propósito: "não pergunte, e negue o que não estiver
 * pré-aprovado" produz sessão que trava sem ter como destravar — nem afrouxa
 * nem contém, só emperra.
 */
export const ModoDoMestreSchema = z.enum([
  'default',
  'acceptEdits',
  'plan',
  'auto',
  'bypassPermissions',
]);

/**
 * Criação de sessão pelo mestre (`mestre.criar_sessao`).
 *
 * O padrão passou de `acceptEdits` para `auto` a pedido do dono ("quero parar
 * de criar sessões pelo mestre em aceitar edições"). `bypassPermissions` nunca
 * é padrão: exige `confirmacaoModoPerigoso` explícito, e a API confere o papel
 * do criador antes de deixar passar.
 *
 * `motor` (ADR-0022, D4) é **opcional e SEM `.default()`**, pela mesma razão
 * do `MotorDaConsultaSchema`: ausente quer dizer "o motor da mestre que
 * cria" — a designação de sempre —, e quem sabe qual é esse motor é a API,
 * que o lê da linha da mestre, nunca do corpo. Um `.default('CLAUDE')` faria
 * a mestre Codex que não falou nada criar sessão Claude. Presente, a API só
 * aceita o da própria mestre ou o OPENCODE: a D4 abriu o OpenCode às mestres
 * Claude e Codex (com o modelo padrão da config, então `modelo` e `esforco`
 * são recusados nele), e não abriu o motor do PAR — sessão do outro motor se
 * pede à outra mestre.
 *
 * Sem `confirmacaoOpencode` aqui, de propósito: a confirmação do OpenCode é
 * gesto de gente (D10), e o dono já a deu para as mestres na D4. Quem a
 * afirma é o SERVIDOR, e só quando o destino é OPENCODE; o modelo não tem
 * como escrevê-la.
 */
export const MestreCriarSessaoSchema = z.object({
  ...Base,
  maquinaId: z.string().min(1),
  cwd: z.string().min(1).max(500),
  nome: z.string().min(1).max(80).optional(),
  motor: z.enum(MOTORES).optional(),
  modo: ModoDoMestreSchema.default('auto'),
  confirmacaoModoPerigoso: z.boolean().default(false),
  modelo: z.string().min(1).optional(),
  esforco: Esforco.optional(),
  primeiraMensagem: z.string().max(LIMITES.textoMensagem).optional(),
});
export type MestreCriarSessao = z.infer<typeof MestreCriarSessaoSchema>;

/**
 * Trocar o modo de uma sessão viva (`mestre.modo`).
 *
 * Vale nos DOIS sentidos: afrouxar (para `auto`/`bypassPermissions`) e
 * APERTAR (de volta para `default`/`plan`). Só afrouxar seria via de mão única,
 * e tirar de uma sessão o portão sem poder devolvê-lo é pior que nunca tê-lo
 * tirado.
 *
 * A troca vale da PRÓXIMA decisão de permissão em diante: o cartão que já está
 * na tela continua pendurado, porque `setPermissionMode` não mexe em pedido
 * pendente. Quem promete "isto destrava a sessão travada" está prometendo
 * errado — e a ferramenta diz isso na resposta.
 */
export const MestreModoSchema = z.object({
  ...Base,
  sessaoId: z.string().min(1),
  modo: ModoDoMestreSchema,
  confirmacaoModoPerigoso: z.boolean().default(false),
});
export type MestreModo = z.infer<typeof MestreModoSchema>;

/**
 * Encerrar uma sessão (`mestre.encerrar`). Mata o processo, e não tem volta.
 *
 * Fronteira NOVA da D50: até aqui encerrar era só do dono (D40). O que segura
 * agora não é a ausência de ferramenta, são as recusas — turno em andamento,
 * pendência esperando decisão, e a própria mestre. `forcar` existe para o caso
 * legítimo, e é explícito de propósito: nunca o padrão.
 */
export const MestreEncerrarSchema = z.object({
  ...Base,
  sessaoId: z.string().min(1),
  forcar: z.boolean().default(false),
});
export type MestreEncerrar = z.infer<typeof MestreEncerrarSchema>;

/**
 * Retomar uma sessão que dorme (`mestre.retomar`).
 *
 * Sem isto, `adormecer_sessao` era porta de mão única: a mestre punha para
 * dormir e dependia de clique humano para acordar — nem despachar acorda
 * (`enviar_mensagem` devolve `sessao_dormindo`). O mecanismo já existia e já
 * era testado, restrito à mestre (`cutucarMestre`); aqui ele deixa de ser
 * privilégio de um caminho só.
 *
 * NÃO se confunde com desarquivar: arquivar mexe na LISTA, dormir e retomar
 * mexem no PROCESSO. Uma sessão pode estar arquivada e viva, ou na lista e
 * dormindo.
 */
export const MestreRetomarSchema = z.object({
  ...Base,
  sessaoId: z.string().min(1),
});
export type MestreRetomar = z.infer<typeof MestreRetomarSchema>;

/**
 * Enviar um arquivo do disco da máquina para a conversa (`mestre.anexo`).
 *
 * É a direção inversa do anexo do painel, e o pedido do dono que a criou foi
 * literal: *"eu enviei lá no chat e você só me devolve caminho de arquivo"*.
 *
 * **Nenhum caminho viaja neste schema, de propósito.** O daemon já resolveu o
 * arquivo com `realpath`, já conferiu que ele está dentro do `cwd` da mestre,
 * já passou pela allowlist forte e pela lista de segredos, e já mediu nome e
 * tamanho NO DISCO. O que sobe é o resultado dessa medição — a API não tem
 * como reconferir um caminho de outra máquina, então pedir que ela confie num
 * caminho seria teatro.
 *
 * `tamanho` é medido, nunca declarado pelo modelo: é o `stat` que o daemon fez
 * antes de abrir o arquivo. A API o usa para recusar acima do teto — e recusa,
 * nunca trunca. Arquivo cortado entregue como inteiro é instrumento que mente.
 */
export const MestreAnexoSchema = z.object({
  ...Base,
  /** Nome-base do arquivo, medido no disco. */
  nome: z.string().min(1).max(200),
  mime: z.string().min(1).max(120),
  /** Bytes, do `stat`. */
  tamanho: z.number().int().positive(),
});
export type MestreAnexo = z.infer<typeof MestreAnexoSchema>;

/**
 * Resposta de qualquer `mestre.*`. O `erro` é um código pt-BR de
 * `CODIGOS_ERRO` (ou `ESCOPO`/`PROTOCOLO`, os dois do canal do agente); a
 * ferramenta MCP o traduz em texto para o modelo.
 */
export const MestreRespostaSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), dados: z.unknown() }),
  z.object({
    ok: z.literal(false),
    erro: z.string().min(1).max(60),
    mensagem: z.string().max(300).optional(),
  }),
]);
export type MestreResposta = z.infer<typeof MestreRespostaSchema>;

/**
 * Leitura curada do `.claude` de uma máquina (`config.claude.ler`, API→daemon).
 *
 * A carga do pedido é vazia de propósito: NENHUM caminho viaja no fio. O
 * daemon lê uma lista fixa de artefatos do próprio `dirConfig` e devolve com
 * segredo redigido — `.credentials.json`/`.claude.json` são inalcançáveis
 * porque não estão na lista, não porque alguém os nega.
 */
export const ConfigClaudeLerSchema = z.object({});

export const ItemDeConfigSchema = z.object({
  /** Caminho relativo ao dirConfig (ex.: `settings.json`, `rules/api.md`). */
  caminho: z.string().min(1).max(300),
  conteudo: z.string(),
  truncado: z.boolean().default(false),
});
export type ItemDeConfig = z.infer<typeof ItemDeConfigSchema>;

export const ConfigClaudeRespostaSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), itens: z.array(ItemDeConfigSchema).max(200) }),
  z.object({
    ok: z.literal(false),
    erro: z.string().min(1).max(60),
    mensagem: z.string().max(300).optional(),
  }),
]);
export type ConfigClaudeResposta = z.infer<typeof ConfigClaudeRespostaSchema>;
